<script setup>
import { computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatDate } = useCurrency()

const props = defineProps({
  booking:   { type: Object, default: null },
  tours:     { type: Array, default: () => [] },
  customers: { type: Array, default: () => [] },
  defaults:  { type: Object, default: () => ({}) },
  errors:    { type: Object, default: () => ({}) },
})

const isEdit = computed(() => !!props.booking?.id)

const form = useForm({
  tour_id:     props.booking?.tour_id || '',
  customer_id: props.booking?.customer_id || '',
  starts_on:   props.booking?.starts_on || '',
  adults:      props.booking?.adults ?? 1,
  children:    props.booking?.children ?? 0,
  notes:       props.booking?.notes || '',
})

const tourOptions = computed(() =>
  props.tours.map(x => ({ value: x.id, label: x.title })))
const customerOptions = computed(() =>
  props.customers.map(c => ({ value: c.id, label: c.full_name })))

const selectedTour = computed(() =>
  props.tours.find(x => x.id === Number(form.tour_id)) || null)

const endHint = computed(() => {
  const tour = selectedTour.value
  if (!tour || !form.starts_on) return ''
  const days = Math.max(Math.ceil(tour.duration_minutes / (60 * 24)), 1)
  const start = new Date(form.starts_on)
  const end = new Date(start)
  end.setDate(end.getDate() + days - 1)
  return t('tourBookings.endHint', { date: formatDate(end.toISOString()), days })
})

const maxGroupHint = computed(() => {
  const m = selectedTour.value?.max_group_size
  return m ? t('tourBookings.maxGroup', { n: m }) : ''
})

function submit() {
  const payload = form.transform(d => ({ tour_booking: d }))
  if (isEdit.value) payload.patch(`/tour_bookings/${props.booking.id}`)
  else payload.post('/tour_bookings')
}
</script>

<template>
  <PageShell
    :title="isEdit ? t('common.edit') : t('tourBookings.new')"
    narrow
    back-href="/tour_calendar"
  >
    <form
      id="tour-booking-form"
      class="space-y-4"
      @submit.prevent="submit"
    >
      <div>
        <label class="block text-sm mb-1 text-[color:var(--inkSoft)]">{{ t('tourBookings.tour') }}</label>
        <UiSelect
          v-model="form.tour_id"
          :options="tourOptions"
          :error="errors.tour || errors.base"
        />
        <p
          v-if="endHint"
          class="mt-1 text-xs text-[color:var(--muted)]"
        >
          {{ endHint }}
        </p>
        <p
          v-if="maxGroupHint"
          class="text-xs text-[color:var(--muted)]"
        >
          {{ maxGroupHint }}
        </p>
      </div>

      <div>
        <label class="block text-sm mb-1 text-[color:var(--inkSoft)]">{{ t('tourBookings.customer') }}</label>
        <UiSelect
          v-model="form.customer_id"
          :options="customerOptions"
          :error="errors.customer"
        />
      </div>

      <div>
        <label class="block text-sm mb-1 text-[color:var(--inkSoft)]">{{ t('tourBookings.dates') }}</label>
        <UiInput
          v-model="form.starts_on"
          type="date"
          :error="form.errors.starts_on"
        />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-sm mb-1 text-[color:var(--inkSoft)]">{{ t('tourBookings.adults') }}</label>
          <UiInput
            v-model="form.adults"
            type="number"
            :error="form.errors.adults"
          />
        </div>
        <div>
          <label class="block text-sm mb-1 text-[color:var(--inkSoft)]">{{ t('tourBookings.children') }}</label>
          <UiInput
            v-model="form.children"
            type="number"
            :error="form.errors.children"
          />
        </div>
      </div>

      <div>
        <label class="block text-sm mb-1 text-[color:var(--inkSoft)]">{{ t('tourBookings.notes') }}</label>
        <UiTextarea
          v-model="form.notes"
          :rows="3"
        />
      </div>
    </form>

    <template #footer>
      <StickyActionBar>
        <UiButton
          type="submit"
          form="tour-booking-form"
          variant="primary"
          :loading="form.processing"
        >
          {{ isEdit ? t('common.save') : t('tourBookings.new') }}
        </UiButton>
      </StickyActionBar>
    </template>
  </PageShell>
</template>
